import { ToolOutlined } from '@ant-design/icons';
import { Collapse, Spin, Tag, Typography, theme } from 'antd';
import Markdown from 'react-markdown';

import type { ConversationMessageDto } from '@clh/api-client';

import humlaBotSrc from './humla-bot.png';

const { Text } = Typography;

interface ChatMessagesProps {
    messages: ConversationMessageDto[];
    isStreaming?: boolean;
}

export function ChatMessages({
    messages,
    isStreaming = false,
}: ChatMessagesProps) {
    const {
        token: { colorBgContainer, borderRadiusLG, colorPrimary },
    } = theme.useToken();

    return (
        <>
            {messages.map((msg, idx) => (
                <div
                    key={msg.id}
                    style={{
                        display: 'flex',
                        justifyContent:
                            msg.role === 'user' ? 'flex-end' : 'flex-start',
                        marginBottom: 16,
                    }}
                >
                    {msg.role === 'user' ? (
                        <div style={{ maxWidth: '75%' }}>
                            {msg.profile && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize: 12,
                                        display: 'block',
                                        textAlign: 'right',
                                        marginBottom: 4,
                                    }}
                                >
                                    {msg.profile.firstName}{' '}
                                    {msg.profile.lastName}
                                </Text>
                            )}
                            <div
                                style={{
                                    padding: '10px 16px',
                                    borderRadius: borderRadiusLG,
                                    background: colorPrimary,
                                    color: '#fff',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    lineHeight: 1.6,
                                }}
                            >
                                {msg.content}
                            </div>
                        </div>
                    ) : (
                        <div
                            style={{
                                maxWidth: '85%',
                                minWidth: 0,
                                display: 'flex',
                                gap: 10,
                            }}
                        >
                            <img
                                src={humlaBotSrc}
                                alt="Humla Bot"
                                style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    flexShrink: 0,
                                    marginTop: 4,
                                }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 8,
                                    flex: 1,
                                }}
                            >
                                {/* Tool calls */}
                                {msg.toolCalls && msg.toolCalls.length > 0 && (
                                    <Collapse
                                        size="small"
                                        style={{
                                            background: colorBgContainer,
                                        }}
                                        items={msg.toolCalls.map((tc) => ({
                                            key: tc.id,
                                            label: (
                                                <span
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 6,
                                                    }}
                                                >
                                                    <ToolOutlined
                                                        style={{
                                                            fontSize: 12,
                                                            color: '#888',
                                                        }}
                                                    />
                                                    <code
                                                        style={{ fontSize: 12 }}
                                                    >
                                                        {tc.name}
                                                    </code>
                                                    {tc.result !== undefined ? (
                                                        <Tag
                                                            color={
                                                                tc.result.startsWith(
                                                                    'Error:'
                                                                )
                                                                    ? 'red'
                                                                    : 'green'
                                                            }
                                                            style={{
                                                                fontSize: 11,
                                                                lineHeight:
                                                                    '18px',
                                                                marginLeft: 4,
                                                            }}
                                                        >
                                                            {tc.result.startsWith(
                                                                'Error:'
                                                            )
                                                                ? 'error'
                                                                : 'done'}
                                                        </Tag>
                                                    ) : (
                                                        <Spin
                                                            size="small"
                                                            style={{
                                                                marginLeft: 4,
                                                            }}
                                                        />
                                                    )}
                                                </span>
                                            ),
                                            children: (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 8,
                                                    }}
                                                >
                                                    <div>
                                                        <Text
                                                            type="secondary"
                                                            style={{
                                                                fontSize: 11,
                                                                textTransform:
                                                                    'uppercase',
                                                                letterSpacing: 0.5,
                                                            }}
                                                        >
                                                            Input
                                                        </Text>
                                                        <pre
                                                            style={{
                                                                fontSize: 12,
                                                                maxHeight: 150,
                                                                overflow:
                                                                    'auto',
                                                                background:
                                                                    '#f5f5f5',
                                                                padding: 8,
                                                                borderRadius: 4,
                                                                margin: '4px 0 0',
                                                            }}
                                                        >
                                                            {JSON.stringify(
                                                                tc.input,
                                                                null,
                                                                2
                                                            )}
                                                        </pre>
                                                    </div>
                                                    {tc.result !==
                                                        undefined && (
                                                        <div>
                                                            <Text
                                                                type="secondary"
                                                                style={{
                                                                    fontSize: 11,
                                                                    textTransform:
                                                                        'uppercase',
                                                                    letterSpacing: 0.5,
                                                                }}
                                                            >
                                                                Result
                                                            </Text>
                                                            <pre
                                                                style={{
                                                                    fontSize: 12,
                                                                    maxHeight: 250,
                                                                    overflow:
                                                                        'auto',
                                                                    background:
                                                                        '#f5f5f5',
                                                                    padding: 8,
                                                                    borderRadius: 4,
                                                                    margin: '4px 0 0',
                                                                }}
                                                            >
                                                                {tc.result}
                                                            </pre>
                                                        </div>
                                                    )}
                                                </div>
                                            ),
                                        }))}
                                    />
                                )}

                                {/* Text content */}
                                {(msg.content ||
                                    !(
                                        isStreaming &&
                                        idx === messages.length - 1
                                    )) && (
                                    <div
                                        style={{
                                            padding: '10px 16px',
                                            borderRadius: borderRadiusLG,
                                            background: colorBgContainer,
                                            wordBreak: 'break-word',
                                            overflowX: 'auto',
                                            lineHeight: 1.6,
                                        }}
                                    >
                                        <Markdown>{msg.content}</Markdown>
                                    </div>
                                )}

                                {/* Streaming indicator */}
                                {isStreaming &&
                                    idx === messages.length - 1 &&
                                    !msg.content && (
                                        <div
                                            style={{
                                                padding: '10px 16px',
                                                borderRadius: borderRadiusLG,
                                                background: colorBgContainer,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 8,
                                            }}
                                        >
                                            <Spin size="small" />
                                            <Text type="secondary">
                                                {msg.toolCalls &&
                                                msg.toolCalls.length > 0
                                                    ? 'Processing...'
                                                    : 'Thinking...'}
                                            </Text>
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </>
    );
}
